<template>
    <AppLayout title="Members Directory">
        <div class="container mt-4">


            <!-- Search Section -->
            <div class="row">
                <div class="col-12">
                    <div class="card mb-4">
                        <div class="card-body">
                            <div class="row align-items-center">
                                <div class="col-md-6">
                                    <h3 class="mb-2">Find ICDGP Members</h3>
                                    <p class="subtitle">Connect with cybersecurity and data governance professionals from around the world.</p>
                                </div>
                                <div class="col-md-6">
                                    <div class="input-group">
                                        <input type="text" class="form-control" placeholder="Search by name, job title, or company..." v-model="search" @keyup.enter="searchMembers">
                                        <button @click="searchMembers" class="btn btn-outline-secondary" type="button">
                                            <i class="bi bi-search"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Stats Summary -->
            <div class="row mb-4 d-none">
                <div class="col-md-4">
                    <div class="stats_card card mb-3">
                        <div class="card-body">
                            <div class="icon">
                                <i class="bi bi-people-fill"></i>
                            </div>
                            <div>
                                <h5 class="mb-0">{{ props.stats.totalMembers || 0 }}</h5>
                                <span class="subtitle">Total Members</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="stats_card card mb-3">
                        <div class="card-body">
                            <div class="icon">
                                <i class="bi bi-globe"></i>
                            </div>
                            <div>
                                <h5 class="mb-0">{{ props.stats.countries || 0 }}</h5>
                                <span class="subtitle">Countries</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="stats_card card mb-3">
                        <div class="card-body">
                            <div class="icon">
                                <i class="bi bi-briefcase-fill"></i>
                            </div>
                            <div>
                                <h5 class="mb-0">{{ props.stats.industries || 0 }}</h5>
                                <span class="subtitle">Industries</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Filters -->
            <div class="row">
                <div class="col-12">
                    <div class="card mb-4">
                        <div class="card-body">
                            <div class="row">
                                <div class="col-md-3 mb-3">
                                    <label class="form-label">Membership Type</label>
                                    <select v-model="filters.membershipType" @change="applyFilters" class="form-select">
                                        <option value="">All Types</option>
                                        <option value="student">Student</option>
                                        <option value="individual">Individual</option>
                                        <option value="corporate">Corporate</option>
                                    </select>
                                </div>
                                <div class="col-md-3 mb-3">
                                    <label class="form-label">Country</label>
                                    <select v-model="filters.country" @change="applyFilters" class="form-select">
                                        <option value="">All Countries</option>
                                        <option v-for="country in availableCountries" :key="country" :value="country">
                                            {{ country }}
                                        </option>
                                    </select>
                                </div>
                                <div class="col-md-3 mb-3">
                                    <label class="form-label">Industry</label>
                                    <select v-model="filters.industry" @change="applyFilters" class="form-select">
                                        <option value="">All Industries</option>
                                        <option v-for="industry in availableIndustries" :key="industry" :value="industry">
                                            {{ industry }}
                                        </option>
                                    </select>
                                </div>
                                <div class="col-md-3 mb-3">
                                    <label class="form-label">Certification Status</label>
                                    <select v-model="filters.certified" @change="applyFilters" class="form-select">
                                        <option value="">All</option>
                                        <option value="true">Certified</option>
                                        <option value="false">Not Certified</option>
                                    </select>
                                </div>
                            </div>
                            <div class="text-end">
                                <button @click="resetFilters" class="btn btn-outline-secondary">
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Members Grid -->
            <div class="row">
                <div class="col-12">
                    <div v-if="loading" class="text-center py-5">
                        <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>

                    <div v-else-if="members.length === 0" class="card">
                        <div class="card-body text-center py-5">
                            <i class="bi bi-people fs-1 text-secondary mb-3"></i>
                            <h4>No members found</h4>
                            <p class="subtitle">Try changing your search or filters to find members.</p>
                        </div>
                    </div>

                    <div v-else>
                        <div class="row">
                            <div v-for="member in members" :key="member.id" class="col-md-6 col-lg-4 mb-4">
                                <div class="card h-100 member-card" @click="viewProfile(member)">

                                    <div class="card-body text-center pt-4">
                                        <h5 class="card-title">{{ member.first_name }} {{ member.last_name }}</h5>
                                        <p class="subtitle mb-3">{{ member.industry || ' ' }}</p>

                                        <div class="d-flex align-items-center justify-content-center mb-2 small">
                                            <i class="bi bi-geo-alt me-1"></i>
                                            <span class="text-muted">{{ member.city || '' }}{{ member.city && member.country ? ', ' : '' }}{{ member.country || 'Location not specified' }}</span>
                                        </div>

                                        <div class="d-flex align-items-center justify-content-center mb-3 small">
                                            <i class="bi bi-briefcase me-1"></i>
                                            <span class="text-muted">{{ member.professional_title || 'Professional title not specified' }}</span>
                                        </div>

                                        <!-- <div class="border-top pt-3 mt-3">
                                            <span class="badge" :class="{
                                                'bg-primary': member.membership_type && member.membership_type.toLowerCase() === 'individual',
                                                'bg-purple': member.membership_type && member.membership_type.toLowerCase() === 'corporate',
                                                'bg-success': member.membership_type && member.membership_type.toLowerCase() === 'student',
                                                'bg-secondary': !member.membership_type
                                            }">
                                                {{ getMembershipType(member.membership_type) }}
                                            </span>
                                        </div> -->
                                    </div>
                                </div>
                            </div>

                            <!-- Infinite Scroll Loading Indicator -->
                            <div v-if="loadingMore" class="text-center py-4">
                                <div class="spinner-border text-primary" role="status">
                                    <span class="visually-hidden">Loading more members...</span>
                                </div>
                                <p class="text-muted mt-2">Loading more members...</p>
                            </div>

                            <!-- End of Results Indicator -->
                            <div v-if="!hasMorePages && members.length > 0" class="text-center py-4">
                                <p class="text-muted">You've reached the end of the member list.</p>
                            </div>

                            <!-- Infinite Scroll Trigger -->
                            <div ref="loadMoreTrigger" class="text-center py-4" v-show="hasMorePages && !loadingMore">
                                <p class="text-muted small">Scroll down to load more members</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <!-- Member Profile Modal -->
            <div v-if="selectedMember" class="modal-overlay" @click.self="closeProfileModal">
                <div class="modal fade show d-block" tabindex="-1" aria-modal="true" style="pointer-events: none;">
                    <div class="modal-dialog modal-lg modal-dialog-centered" style="pointer-events: auto;">
                        <div class="modal-content">

                            <div class="modal-body">

                                <!-- Name and Membership Type-->
                                <div class="d-flex justify-content-between align-items-center mb-4 mt-4">
                                    <div>
                                        <h4 class="mb-1">{{ selectedMember.first_name }} {{ selectedMember.last_name }}</h4>
                                        <p class="subtitle d-none">{{ selectedMember.professionional_title || 'Member' }}</p>
                                    </div>

                                    <!-- <div>
                                        <span class="badge" :class="{
                                            'bg-primary': selectedMember.membership_type && selectedMember.membership_type.toLowerCase() === 'individual',
                                            'bg-purple': selectedMember.membership_type && selectedMember.membership_type.toLowerCase() === 'corporate',
                                            'bg-success': selectedMember.membership_type && selectedMember.membership_type.toLowerCase() === 'student',
                                            'bg-secondary': !selectedMember.membership_type
                                        }">
                                            {{ getMembershipType(selectedMember.membership_type) }}
                                        </span>
                                    </div> -->
                                </div>
                                <hr>

                                <!-- Member Details-->
                                <div class="card mb-4">
                                    <div class="card-body">
                                        <div class="row">
                                            <div class="col-md-6 mb-3">
                                                <div class="d-flex align-items-center">
                                                    <i class="bi bi-geo-alt me-2 text-secondary"></i>
                                                    <span class="d-none">{{ selectedMember.city || '' }}{{ selectedMember.city && selectedMember.country ? ', ' : '' }}{{ selectedMember.country || 'Location not specified' }}</span>
                                                    <span>{{ selectedMember.country || 'Location not specified' }}</span>
                                                </div>
                                            </div>

                                            <div class="col-md-6 mb-3">
                                                <div class="d-flex align-items-center">
                                                    <i class="bi bi-building me-2 text-secondary"></i>
                                                    <span>{{ selectedMember.company || 'Company not specified' }}</span>
                                                </div>
                                            </div>

                                            <div class="col-md-6 mb-3">
                                                <div class="d-flex align-items-center">
                                                    <i class="bi bi-briefcase me-2 text-secondary"></i>
                                                    <span>{{ selectedMember.industry || 'Industry not specified' }}</span>
                                                </div>
                                            </div>

                                            <div v-if="selectedMember.email" class="col-md-6 mb-3 d-none">
                                                <div class="d-flex align-items-center">
                                                    <i class="bi bi-envelope me-2 text-secondary"></i>
                                                    <span>{{ selectedMember.email }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- About -->
                                <div class="card mb-4 d-none">
                                    <div class="card-header">
                                        <h5 class="mb-0">About</h5>
                                    </div>
                                    <div class="card-body">
                                        <p class="subtitle">{{ selectedMember.bio || 'No biography provided.' }}</p>
                                    </div>
                                </div>




                                <!-- Certificates -->
                                <div v-if="selectedMember.certificates && selectedMember.certificates.length > 0" class="card mb-4 d-none">
                                    <div class="card-header">
                                        <h5 class="mb-0">Certificates</h5>
                                    </div>
                                    <div class="card-body">
                                        <div class="row">
                                            <div class="col-md-6" v-for="(certificate, index) in selectedMember.certificates" :key="certificate.id" :class="{ 'mb-3': index < selectedMember.certificates.length - 1 }">
                                                <div>
                                                    <div class="d-flex align-items-start bg-light p-3 rounded">
                                                        <div class="me-3">
                                                            <i class="bi bi-award-fill fs-4 text-success"></i>
                                                        </div>
                                                        <div class="flex-grow-1">
                                                            <div class="fw-medium">{{ formatCertificateType(certificate.certificate_type) }}</div>
                                                            <div class="subtitle mb-1">Certificate #: {{ certificate.certificate_number }}</div>
                                                            <div class="subtitle">Issued: {{ formatDate(certificate.issue_date) }}</div>
                                                            <div v-if="certificate.expiry_date" class="subtitle">
                                                                Expires: {{ formatDate(certificate.expiry_date) }}
                                                            </div>
                                                            <span v-if="certificate.status" class="badge mt-2" :class="{
                                                                'bg-success': certificate.status === 'active',
                                                                'bg-warning': certificate.status === 'pending',
                                                                'bg-danger': certificate.status === 'expired',
                                                                'bg-secondary': certificate.status === 'revoked'
                                                            }">
                                                                {{ certificate.status }}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                <div v-if="selectedMember.expertise && selectedMember.expertise.length > 0" class="card mb-4 d-none">
                                    <div class="card-header">
                                        <h5 class="mb-0">Areas of Expertise</h5>
                                    </div>
                                    <div class="card-body">
                                        <div class="d-flex flex-wrap gap-2">
                                            <span v-for="(skill, index) in selectedMember.expertise" :key="index" class="badge bg-light text-dark">
                                                {{ skill }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Connection Request Section -->
                                <div class="card mt-5 mb-0">
                                    <div class="card-header">
                                        <h5 class="mb-0">Send Connection Request</h5>
                                    </div>
                                    <div class="card-body">
                                        <p class="subtitle mb-3 mt-4">Send a connection request to {{ selectedMember.first_name }}. They will receive an email with your contact information.</p>
                                        <form @submit.prevent="sendConnectionRequest">
                                            <div class="mb-3">

                                                <textarea id="connection-message" v-model="connectionMessage" class="form-control" rows="5" placeholder="Introduce yourself or explain why you'd like to connect..." maxlength="5000" required></textarea>
                                                <small class="text-muted d-none">{{ connectionMessage.length }}/5000 characters</small>
                                            </div>
                                            <div class="d-flex justify-content-between align-items-center">
                                                <button type="button" class="btn btn-secondary" @click="closeProfileModal">Close</button>
                                                <button type="submit" class="btn btn-primary" :disabled="sendingConnection">
                                                    <span v-if="sendingConnection">
                                                        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Sending...
                                                    </span>
                                                    <span v-else>
                                                        <i class="bi bi-send me-2"></i>Send Connection Request
                                                    </span>
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                            <div class="modal-footer d-none">
                                <button type="button" class="btn btn-primary">Connect</button>
                                <button type="button" class="btn btn-secondary" @click="closeProfileModal">Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div> <!-- Close main container -->
    </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick, toRaw } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import { useNotifications } from '@/composables/useNotifications';

// Initialize notifications
const { success, error } = useNotifications();
const page = usePage();

// Props with server data
const props = defineProps({
    members: Object,
    filters: Object,
    stats: Object,
    availableCountries: Array,
    availableIndustries: Array
});

// State variables
const search = ref(props.filters?.search || '');
const loading = ref(false);
const loadingMore = ref(false);
const selectedMember = ref(null);
const connectionMessage = ref('');
const sendingConnection = ref(false);
const loadMoreTrigger = ref(null);

// Extract member data from props
const members = ref(props.members?.data || []);
const currentPage = ref(props.members?.current_page || 1);
const hasMorePages = ref(props.members?.current_page < props.members?.last_page);
const totalPages = ref(props.members?.last_page || 1);

// Intersection Observer for infinite scroll
let observer = null;

// Filters
const filters = ref({
    membershipType: props.filters?.membership_type || '',
    country: props.filters?.country || '',
    industry: props.filters?.industry || '',
    certified: props.filters?.certified || '',
});

// Setup Intersection Observer for infinite scroll
const setupIntersectionObserver = () => {
    if (observer) {
        observer.disconnect();
    }

    observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && hasMorePages.value && !loadingMore.value && !loading.value) {
                    loadMoreMembers();
                }
            });
        },
        {
            rootMargin: '100px', // Start loading 100px before the trigger element is visible
        }
    );

    if (loadMoreTrigger.value) {
        observer.observe(loadMoreTrigger.value);
    }
};

// Load more members for infinite scroll
const loadMoreMembers = () => {
    if (!hasMorePages.value || loadingMore.value) return;

    loadingMore.value = true;
    const nextPage = currentPage.value + 1;

    console.log('Loading more members - current page:', currentPage.value, 'next page:', nextPage);

    // Prepare data for the next page
    const loadData = {
        page: nextPage
    };

    if (search.value) {
        loadData.search = search.value;
    }

    if (filters.value.membershipType) {
        loadData.membership_type = filters.value.membershipType;
    }

    if (filters.value.country) {
        loadData.country = filters.value.country;
    }

    if (filters.value.industry) {
        loadData.industry = filters.value.industry;
    }

    if (filters.value.certified !== '') {
        loadData.certified = filters.value.certified;
    }

    router.visit('/members', {
        method: 'get',
        data: loadData,
        preserveState: true,
        preserveScroll: true,
        only: ['members'],
        onSuccess: (page) => {
            loadingMore.value = false;
            const newMembers = page.props.members.data;
            const newCurrentPage = page.props.members.current_page;
            const newTotalPages = page.props.members.last_page;

            console.log('Infinite scroll success - requested page:', nextPage, 'received page:', newCurrentPage, 'total pages:', newTotalPages);

            // Only append if the backend returned a newer page
            if (newCurrentPage > currentPage.value) {
                if (newMembers.length > 0) {
                    members.value = [...members.value, ...newMembers];
                    currentPage.value = newCurrentPage;
                    totalPages.value = newTotalPages;
                    hasMorePages.value = newCurrentPage < newTotalPages;
                    console.log('Loaded more members, total now:', members.value.length, 'has more pages:', hasMorePages.value);
                } else {
                    hasMorePages.value = false;
                    console.warn('No more members to load.');
                }
            } else if (newCurrentPage === currentPage.value) {
                // If backend returns the same page number, do not append but allow further loads
                console.warn('No new data: backend returned the same page number', newCurrentPage);
            } else {
                // If backend returns a non-sequential page, update current page and continue
                if (newMembers.length > 0) {
                    members.value = [...members.value, ...newMembers];
                    currentPage.value = newCurrentPage;
                    totalPages.value = newTotalPages;
                    hasMorePages.value = newCurrentPage < newTotalPages;
                    console.warn('Unexpected page jump: expected', currentPage.value + 1, 'but got', newCurrentPage, '. Correcting and continuing.');
                } else {
                    hasMorePages.value = false;
                }
            }
        },
        onError: (errors) => {
            loadingMore.value = false;
            console.error('Load more error:', errors);
        },
        onFinish: () => {
            loadingMore.value = false;
        }
    });
};

// Search members
const searchMembers = () => {
    loading.value = true;

    // Prepare search data, only include non-empty values
    const searchData = {
        page: 1
    };

    if (search.value) {
        searchData.search = search.value;
    }

    if (filters.value.membershipType) {
        searchData.membership_type = filters.value.membershipType;
    }

    if (filters.value.country) {
        searchData.country = filters.value.country;
    }

    if (filters.value.industry) {
        searchData.industry = filters.value.industry;
    }

    if (filters.value.certified !== '') {
        searchData.certified = filters.value.certified;
    }

    console.log('Searching with data:', searchData);

    router.visit('/members', {
        data: searchData,
        preserveState: true,
        preserveScroll: true,
        only: ['members', 'filters', 'stats', 'availableCountries', 'availableIndustries'],
        onSuccess: () => {
            loading.value = false;
            const page = usePage();
            // Reset members list for new search and update pagination state
            members.value = page.props.members.data;
            currentPage.value = page.props.members.current_page;
            totalPages.value = page.props.members.last_page;
            hasMorePages.value = page.props.members.current_page < page.props.members.last_page;

            // Reinitialize tooltips after data update
            initTooltips();
            // Setup intersection observer for the new results
            nextTick(() => {
                setupIntersectionObserver();
            });
        },
        onError: (errors) => {
            loading.value = false;
            console.error('Search error:', errors);
        },
        onFinish: () => {
            loading.value = false;
        }
    });
};

// Apply filters
const applyFilters = () => {
    loading.value = true;

    // Prepare filter data, only include non-empty values
    const filterData = {
        page: 1
    };

    if (search.value) {
        filterData.search = search.value;
    }

    if (filters.value.membershipType) {
        filterData.membership_type = filters.value.membershipType;
    }

    if (filters.value.country) {
        filterData.country = filters.value.country;
    }

    if (filters.value.industry) {
        filterData.industry = filters.value.industry;
    }

    if (filters.value.certified !== '') {
        filterData.certified = filters.value.certified;
    }

    console.log('Applying filters with data:', filterData);

    router.visit('/members', {
        method: 'get',
        data: filterData,
        preserveState: true,
        preserveScroll: true,
        only: ['members', 'filters', 'stats', 'availableCountries', 'availableIndustries'],
        onStart: () => {
            console.log('Filter request starting...');
        },
        onSuccess: () => {
            loading.value = false;
            const page = usePage();
            // Reset members list for new filters and update pagination state
            members.value = page.props.members.data;
            currentPage.value = page.props.members.current_page;
            totalPages.value = page.props.members.last_page;
            hasMorePages.value = page.props.members.current_page < page.props.members.last_page;
            console.log('Filter success, updated members:', members.value.length);

            // Reinitialize tooltips after data update
            initTooltips();
            // Setup intersection observer for the new results
            nextTick(() => {
                setupIntersectionObserver();
            });
        },
        onError: (errors) => {
            loading.value = false;
            console.error('Filter error:', errors);
        },
        onFinish: () => {
            loading.value = false;
        }
    });
};

// Reset filters
const resetFilters = () => {
    filters.value = {
        membershipType: '',
        country: '',
        industry: '',
        certified: '',
    };
    search.value = '';
    loading.value = true;

    router.visit('/members', {
        method: 'get',
        only: ['members', 'filters', 'stats', 'availableCountries', 'availableIndustries'],
        preserveScroll: true,
        onSuccess: () => {
            loading.value = false;
            const page = usePage();
            // Reset members list and update pagination state
            members.value = page.props.members.data;
            currentPage.value = page.props.members.current_page;
            totalPages.value = page.props.members.last_page;
            hasMorePages.value = page.props.members.current_page < page.props.members.last_page;
            console.log('Reset filters success, updated members:', members.value.length);

            // Setup intersection observer for the reset results
            nextTick(() => {
                setupIntersectionObserver();
            });
        },
        onError: (errors) => {
            loading.value = false;
            console.error('Reset filters error:', errors);
        },
        onFinish: () => {
            loading.value = false;
        }
    });
};

// View member profile
const viewProfile = (member) => {
    selectedMember.value = { ...member };
    connectionMessage.value = ''; // Reset message when opening modal
    console.log('Viewing profile for member:', toRaw(selectedMember.value));
};

// Close profile modal
const closeProfileModal = () => {
    selectedMember.value = null;
    connectionMessage.value = ''; // Reset message when closing modal
};

// Send connection request
const sendConnectionRequest = () => {
    if (!selectedMember.value) {
        error('No member selected');
        return;
    }

    console.log('Sending connection request to:', selectedMember.value.first_name, selectedMember.value.last_name);
    sendingConnection.value = true;

    router.post('/members/connect', {
        member_id: selectedMember.value.id,
        message: connectionMessage.value || null
    }, {
        preserveScroll: true,
        preserveState: true,
        onSuccess: (page) => {
            console.log('Connection request sent successfully');
            sendingConnection.value = false;
            connectionMessage.value = '';

            // Show success notification
            if (page.props.flash?.success) {
                success(page.props.flash.success);
            } else {
                success(`Connection request sent to ${selectedMember.value.first_name} ${selectedMember.value.last_name}!`);
            }

            closeProfileModal();
        },
        onError: (errors) => {
            console.error('Connection request failed:', errors);
            sendingConnection.value = false;

            // Show error notification
            if (errors.message) {
                error(errors.message);
            } else if (typeof errors === 'string') {
                error(errors);
            } else if (page.props.flash?.error) {
                error(page.props.flash.error);
            } else {
                error('Failed to send connection request. Please try again.');
            }
        },
        onFinish: () => {
            console.log('Connection request finished');
            sendingConnection.value = false;
        }
    });
};// Format date
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

// Get membership type label
const getMembershipType = (type) => {
    if (!type) return 'Standard Member';

    // Convert to lowercase for case-insensitive matching
    const normalizedType = type.toLowerCase();

    const types = {
        'student': 'Student Member',
        'individual': 'Individual Member',
        'corporate': 'Corporate Member'
    };

    return types[normalizedType] || 'Standard Member';
};

// Format certificate type
const formatCertificateType = (type) => {
    if (!type) return 'Professional Certificate';

    // Convert to lowercase and replace underscores/hyphens with spaces
    const formatted = type.toLowerCase()
        .replace(/[_-]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    return formatted + ' Certificate';
};

// Watch for prop changes to update local state
watch(() => props.members, (newMembers) => {
    console.log('Members prop changed:', newMembers);
    // Only update if this is the first page (not appending for infinite scroll)
    if (newMembers?.current_page === 1) {
        members.value = newMembers?.data || [];
        currentPage.value = newMembers?.current_page || 1;
        totalPages.value = newMembers?.last_page || 1;
        hasMorePages.value = (newMembers?.current_page || 1) < (newMembers?.last_page || 1);
    }
    // For infinite scroll loads (page > 1), we handle updates in the loadMoreMembers success handler
    // Tooltips removed - not needed on this page
}, { deep: true });

// Initialize data when component mounts
onMounted(() => {
    console.log('Component mounted with props:', props);

    // Initialize members from props
    members.value = props.members?.data || [];
    currentPage.value = props.members?.current_page || 1;
    totalPages.value = props.members?.last_page || 1;
    hasMorePages.value = props.members?.current_page < props.members?.last_page;

    // Initialize filters from props
    if (props.filters) {
        filters.value.membershipType = props.filters.membership_type || '';
        filters.value.country = props.filters.country || '';
        filters.value.industry = props.filters.industry || '';
        filters.value.certified = props.filters.certified || '';
        search.value = props.filters.search || '';

        console.log('Initialized filters:', filters.value);
    }

    // Debug available countries and industries
    console.log('Available countries:', props.availableCountries);
    console.log('Available industries:', props.availableIndustries);

    // Setup intersection observer for infinite scroll
    nextTick(() => {
        setupIntersectionObserver();
    });
});

// Cleanup intersection observer when component unmounts
onUnmounted(() => {
    if (observer) {
        observer.disconnect();
    }
});

// Remove tooltip watchers - not needed on this page

</script>

<style scoped>
/* Add Bootstrap Icons */
@import url("https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.0/font/bootstrap-icons.css");

/* Container styling */
.container {
    max-width: 1200px;
    width: 100%;
    padding-right: var(--bs-gutter-x, 0.75rem);
    padding-left: var(--bs-gutter-x, 0.75rem);
    margin-right: auto;
    margin-left: auto;
}

/* Custom styling */
.member-card {
    transition: all 0.3s ease;
    cursor: pointer;
}

.member-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.form-control:focus {
    box-shadow: none !important;
}

/* Fix modal overlay */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1050;
    overflow-x: hidden;
    overflow-y: auto;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-overlay .modal {
    position: relative;
    z-index: 1055;
}

.modal-overlay .modal-dialog {
    margin: auto;
}

/* Badge colors */
.bg-purple {
    background-color: #6f42c1;
    color: white;
}

.stats_card .icon {
    background-color: rgba(30, 149, 121, 0.1);
    color: var(--primary);
}

/* Force all elements to use Bootstrap's border-radius */
.card,
.form-control,
.form-select,
.btn,
.badge {
    border-radius: 0.375rem !important;
}

/* Certificate badge styling */
.certificate-badge {
    position: absolute;
    bottom: -5px;
    right: -5px;
    background-color: #198754;
    border-radius: 50%;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
    z-index: 2;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}
</style>
